import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { useAuth } from "./auth";

/**
 * Per-dashboard visibility flags controlled by admins.
 *
 * - Admins always see every dashboard (so they can toggle them).
 * - Non-admins follow the saved flags; hidden dashboards disappear from the
 *   sidebar and direct URLs redirect to the home page.
 */
const DEFAULT_FLAGS = { crm_overview: true, leads_performance: true, predictive: true, scorecard: true, sales_performance: true };

const VisibilityCtx = createContext({
  flags: DEFAULT_FLAGS,
  isAdmin: false,
  loading: true,
  refresh: () => Promise.resolve(),
});

export function VisibilityProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/settings/dashboard-visibility");
      // Backend now merges per-user override + global — response is
      // always the effective flag map for THIS user.
      setFlags({ ...DEFAULT_FLAGS, ...(r.data.flags || {}) });
      setIsAdmin(!!r.data.is_admin);
    } catch {
      // silent fallback — keep defaults so we never lock users out.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) refresh();
    else setLoading(false);
  }, [user]);

  return (
    <VisibilityCtx.Provider value={{ flags, isAdmin, loading, refresh }}>
      {children}
    </VisibilityCtx.Provider>
  );
}

export const useDashboardVisibility = () => useContext(VisibilityCtx);
